import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { usePaymentsStore } from '@/features/payments/paymentsStore';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  /** Optional extended profile fields populated from the backend response. */
  firstName?: string;
  lastName?: string;
  username?: string;
  avatarUrl?: string;
  role?: string;
  schoolId?: string;
  schoolName?: string;
  /** Backend account status flag; false means the account is disabled. */
  status?: boolean;
}

/** Arbitrary key/value bag holding the full raw user object returned by the API. */
export type RawAuthUser = Record<string, unknown>;

interface AuthState {
  user: AuthUser | null;
  /** The unmodified user object the backend returned (for screens that need extra fields). */
  rawUser: RawAuthUser | null;
  token: string | null;
  /** Epoch ms when the session was last persisted. */
  loggedInAt: number | null;
  isAuthenticated: boolean;
  setAuth: (user: AuthUser, token: string, rawUser?: RawAuthUser) => void;
  /** Replace the persisted raw user payload (e.g. after a profile re-fetch). */
  setRawUser: (rawUser: RawAuthUser | null) => void;
  updateUser: (patch: Partial<AuthUser>) => void;
  logout: () => void;
}

export const AUTH_STORAGE_KEY = 'auth-store';
export const AUTH_TOKEN_STORAGE_KEY = 'auth_token';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      rawUser: null,
      token: null,
      loggedInAt: null,
      isAuthenticated: false,
      setAuth: (user, token, rawUser) => {
        usePaymentsStore.getState().clearCart();
        localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
        set({
          user,
          rawUser: rawUser ?? null,
          token,
          loggedInAt: Date.now(),
          isAuthenticated: true,
        });
      },
      updateUser: (patch) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...patch } : state.user,
        })),
      setRawUser: (rawUser) => set({ rawUser }),
      logout: () => {
        const prevUserId = get().user?.id;
        usePaymentsStore.getState().clearCart();
        // Reset in-memory auth state synchronously so route guards / UI react
        // immediately. The async cache wipe runs in the background — we don't
        // want to block the redirect on IndexedDB.
        set({
          user: null,
          rawUser: null,
          token: null,
          loggedInAt: null,
          isAuthenticated: false,
        });
        void import('@/lib/clearAppCache').then((m) =>
          m.clearAppCache({ userId: prevUserId ? String(prevUserId) : null }),
        );
      },
    }),
    { name: AUTH_STORAGE_KEY },
  ),
);
